import { candidateButton } from "./handwriting-elements.js";
import { handwritingCandidates } from "./handwriting-answer.js";
import type { HandwritingCandidate } from "../handwriting/handwriting-lookup.js";
import type { HandwritingPad } from "./handwriting-pad.js";
import type { HandwritingSource } from "./handwriting-source.js";

/**
 * The row of characters under the square, and what is said above it.
 *
 * Apart from `handwriting-panel.ts` so that the panel is what happens and this
 * is what a reader sees of it. Both halves are small and neither wants the
 * other's reasons in it.
 */

/** What the row says when there is nothing on the square yet. */
export const nothingDrawnYet = "Draw a character to see what it might be.";

/** What it says when there is, and the index knows none of it. */
export const nothingMatches = "No character matches those strokes yet.";

/** What it says when there is a row to press. */
export const pressToAdd = "Press a character to add it.";

/** Where the characters go, and what is said above them. */
export interface CandidateRow {
  /** The row itself. */
  readonly candidates: HTMLElement;

  /** The line above it, which a reader hearing the page is told. */
  readonly message: HTMLElement;
}

/** Empties the row, and says there is nothing to fill it with. */
export function drawNoCandidates(row: CandidateRow): void {
  row.candidates.replaceChildren();
  row.message.textContent = nothingDrawnYet;
}

/**
 * Draws the characters a reader may have written.
 *
 * `take` is what a press does with one. The panel adds it to the search box
 * and empties the square, which is what lets a reader write 汉 and then 字
 * without finding the pen again.
 */
export function drawCandidates(properties: {
  /** Where they go. */
  readonly row: CandidateRow;

  /** What to draw, best first. */
  readonly found: readonly HandwritingCandidate[];

  /** What a press does. */
  readonly take: (character: string) => void;
}): void {
  const { row, found, take } = properties;

  row.message.textContent = found.length === 0 ? nothingMatches : pressToAdd;
  row.candidates.replaceChildren(
    ...found.map((candidate) => {
      const button = candidateButton(
        row.candidates.ownerDocument,
        candidate.character,
      );

      button.addEventListener("click", () => {
        take(candidate.character);
      });

      return button;
    }),
  );
}

/**
 * Answers the ink on the square with the characters it might be.
 *
 * The index is a fetch, so a reader can empty the square before the answer to
 * the stroke they have just taken back arrives. Drawing that answer would put
 * characters under an empty square, which is what the second look costs one
 * line to avoid.
 */
export async function offerCandidates(properties: {
  /** Where the characters go. */
  readonly row: CandidateRow;

  /** The square, which is asked twice whether anything is on it. */
  readonly pad: HandwritingPad;

  /** Where the index comes from. */
  readonly source: HandwritingSource;

  /** What a press on a character does. */
  readonly take: (character: string) => void;
}): Promise<void> {
  const { row, pad, source, take } = properties;

  if (pad.isEmpty) {
    drawNoCandidates(row);

    return;
  }

  const found = await handwritingCandidates({ source, strokes: pad.strokes });

  if (!pad.isEmpty) {
    drawCandidates({ row, found, take });
  }
}
